<template>
    <v-container fluid>
      <v-row>
        <v-select
          v-model="selectedAcademicBackground"
          :items="academicBackground"
          placeholder="Select your academic background"
          outlined
          filled
          :error-messages="showCustomAcademicBackground? [] : academicBackgroundErrors"
          @blur="$v.background.$touch"
          @change="academicBackgroundChanged"
          :loading="savingAcademicBackground"
        />
      </v-row>
      <v-row v-if="showCustomAcademicBackground">
        <v-text-field
          v-model="background"
          @change="customAcademicBackgroundChanged"
          @click:clear="customAcademicBackgroundChanged(null)"
          clearable
          outlined
          :error-messages="academicBackgroundErrors"
          @blur="$v.background.$touch"
          :loading="savingAcademicBackground"
          placeholder="Academic Background"
        />
      </v-row>

      <v-row align="start" justify="center">
        <v-col>
          <v-card>
            <v-list>
              <v-list-item-group color="accent" v-model="item">
                <v-list-item
                  v-for="(degree, index) in degrees"
                  :key="index"
                  :class="listItemClass(index)"
                  @click="degreeItemClicked(degree)"
                >
                  <v-list-item-content>
                    <v-list-item-title v-text="degree.title"></v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </v-card>
        </v-col>

        <v-col v-if="selectedItemKey === 'bachelor'" class="ml-8">
          <v-row justify="start" align="center">
            <v-col>Upload your Bachelors Degree certificate</v-col>
            <v-col v-if="!degreeFileId">
              <v-file-input
                accept="application/pdf, application/msword"
                prepend-icon="mdi-file-upload-outline"
                :loading="uploadingFile"
                :error-messages="bachelorDegreeErrors"
                @change="onFileChanged($event,'degreeFileId','degree')"
              />
            </v-col>
            <v-col v-else>
              <v-text-field
                clear-icon="mdi-trash-can-outline"
                append-outer-icon="mdi-open-in-new"
                @click:append-outer="onFileOpen('degree')"
                @click:clear="onFileCleared('degreeFileId','degree')"
                v-model="bachelorDegreeFileName"
                readonly
                clearable
              />
            </v-col>
          </v-row>
          <v-row justify="center" align="center">
            <v-col>Upload your Bachelors Degree transcript</v-col>
            <v-col v-if="!degreeTranscriptFileId">
              <v-file-input
                accept="application/pdf, application/msword"
                prepend-icon="mdi-file-upload-outline"
                :loading="uploadingFile"
                :error-messages="bachelorDegreeTranscriptErrors"
                @change="onFileChanged($event,'degreeTranscriptFileId','degreeTranscript')"
              />
            </v-col>
            <v-col v-else>
              <v-text-field
                clear-icon="mdi-trash-can-outline"
                append-outer-icon="mdi-open-in-new"
                @click:append-outer="onFileOpen('degreeTranscript')"
                @click:clear="onFileCleared('degreeTranscriptFileId','degreeTranscript')"
                v-model="bachelorDegreeTranscriptFileName"
                readonly
                clearable
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col v-else-if="selectedItemKey === 'master'" class="ml-8">
          <v-row>
            <v-checkbox
              v-model="hasMasters"
              @change="onTextChange('hasMasters', $event)"
              label="I have completed my masters degree "
            />
          </v-row>
          <v-row justify="center" align="center">
            <v-col v-if="hasMasters">Upload your Masters Degree certificate</v-col>
            <v-col v-else>Upload your Masters Degree proof of enrollment</v-col>
            <v-col v-if="!mastersFileId">
              <v-file-input
                prepend-icon="mdi-file-upload-outline"
                accept="application/pdf, application/msword"
                :loading="uploadingFile"
                :error-messages="mastersDegreeErrors"
                @change="onFileChanged($event,'mastersFileId','masters')"
              />
            </v-col>
            <v-col v-else>
              <v-text-field
                clear-icon="mdi-trash-can-outline"
                append-outer-icon="mdi-open-in-new"
                @click:append-outer="onFileOpen('masters')"
                @click:clear="onFileCleared('mastersFileId','masters')"
                v-model="mastersDegreeFileName"
                readonly
                clearable
              />
            </v-col>
          </v-row>
          <v-row justify="center" align="center" v-if="hasMasters">
            <v-col>Upload your Masters Degree transcript</v-col>
            <v-col v-if="!mastersTranscriptFileId">       
              <v-file-input
                accept="application/pdf, application/msword"
                prepend-icon="mdi-file-upload-outline"
                :loading="uploadingFile"
                :error-messages="mastersDegreeTranscriptErrors"
                @change="onFileChanged($event,'mastersTranscriptFileId','mastersTranscript')"
              />
            </v-col>
            <v-col v-else>
              <v-text-field
                clear-icon="mdi-trash-can-outline"
                append-outer-icon="mdi-open-in-new"
                @click:append-outer="onFileOpen('mastersTranscript')"
                @click:clear="onFileCleared('mastersTranscriptFileId','mastersTranscript')"
                v-model="mastersDegreeTranscriptFileName"
                readonly
                clearable
              />
            </v-col>
          </v-row>
          <v-row>
            <div
              class="primaryText--text caption"
            >* If you have completed more than one masters degree, please upload them as a single document</div>
          </v-row>
        </v-col>

        <v-col v-else-if="selectedItemKey === 'phd'" class="ml-8">
          <v-row>
            <v-checkbox
              v-model="hasPhd"
              @change="onTextChange('hasPhd', $event)"
              label="I have completed my PhD"
            />
          </v-row>
          <v-row justify="center" align="center">
            <v-col v-if="hasPhd">Upload PhD certificate</v-col>
            <v-col v-else>Upload PhD proof of enrollment</v-col>
            <v-col v-if="!phdFileId">
              <v-file-input
                accept="application/pdf, application/msword"
                prepend-icon="mdi-file-upload-outline"
                :loading="uploadingFile"
                @change="onFileChanged($event,'phdFileId','phd')"
              />
            </v-col>
            <v-col v-else>
              <v-text-field
                clear-icon="mdi-trash-can-outline"
                append-outer-icon="mdi-open-in-new"
                @click:append-outer="onFileOpen('phd')"
                @click:clear="onFileCleared('phdFileId','phd')"
                v-model="phdFileName"
                readonly
                clearable
              />
            </v-col>
          </v-row>
          <v-row justify="center" align="center">
            <v-col>PhD transripts</v-col>
            <v-col v-if="!phdTranscriptFileId">
              <v-file-input
                accept="application/pdf, application/msword"
                prepend-icon="mdi-file-upload-outline"
                :loading="uploadingFile"
                @change="onFileChanged($event,'phdTranscriptFileId','phdTranscript')"
              />
            </v-col>
            <v-col v-else>
              <v-text-field
                clear-icon="mdi-trash-can-outline"
                append-outer-icon="mdi-open-in-new"
                @click:append-outer="onFileOpen('phdTranscript')"
                @click:clear="onFileCleared('phdTranscriptFileId','phdTranscript')"
                v-model="phdTranscriptFileName"
                readonly
                clearable
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { required, requiredIf } from "vuelidate/lib/validators";

// always use lodash modules and avoid bringing all lodash
// library to the project
import includes from "lodash.includes";

import submissionMixin from "../mixins/submission.mixin";


export default {
  name: "Education",
  created() {
    this.setupAcademicBackground();
  },
  data() {
    return {
      item: 0,
      savingAcademicBackground: false,
      selectedItemKey: "bachelor",
      selectedAcademicBackground: "",
      academicBackground: [
        "Engineering",
        "Business / Economics",
        "Law",
        "IT",
        "Other"
      ],
      degrees: [
        {
          key: "bachelor",
          title: "Bachelor Degree"
        },
        {
          key: "master",
          title: "Masters Degree"
        },
        {
          key: "phd",
          title: "PhD (Optional)"
        }
      ]
    };
  },
  validations: {
    bachelorDegreeFileName: {
      required
    },
    bachelorDegreeTranscriptFileName: {
      required
    },
    mastersDegreeFileName: {
      required
    },
    mastersDegreeTranscriptFileName: {
      required: requiredIf(function(val){
        return this.hasMasters
      })
    },
    background: {
      required
    }
  },
  computed: {
    ...mapFields("profile", {
      profile: "profile",
      background: "profile.background",

      degreeFileId: "profile.degreeFileId",
      degree: "profile.degree",
      degreeTranscriptFileId: "profile.degreeTranscriptFileId",
      degreeTranscript: "profile.degreeTranscript",
      bachelorDegreeFileName: "profile.degree.originalName",
      bachelorDegreeTranscriptFileName: "profile.degreeTranscript.originalName",

      hasMasters: "profile.hasMasters",
      mastersFileId: "profile.mastersFileId",
      masters: "profile.masters",
      mastersTranscriptFileId: "profile.mastersTranscriptFileId",
      mastersTranscript: "profile.mastersTranscript",
      mastersDegreeFileName: "profile.masters.originalName",
      mastersDegreeTranscriptFileName: "profile.mastersTranscript.originalName",

      hasPhd: "profile.hasPhd",
      phdFileId: "profile.phdFileId",
      phd: "profile.phd",
      phdTranscript: "profile.phdTranscript",
      phdTranscriptFileId: "profile.phdTranscriptFileId",
      phdFileName: "profile.phd.originalName",
      phdTranscriptFileName: "profile.phdTranscript.originalName"
    }),
    showCustomAcademicBackground() {
      return this.selectedAcademicBackground === "Other";
    },

    bachelorDegreeErrors() {
      const errors = [];
      if (!this.$v.bachelorDegreeFileName.$dirty) return errors;
      !this.$v.bachelorDegreeFileName.required &&
        errors.push("Your bachelor degree certificate is required.");
      return errors;
    },

    bachelorDegreeTranscriptErrors() {
      const errors = [];
      if (!this.$v.bachelorDegreeTranscriptFileName.$dirty) return errors;
      !this.$v.bachelorDegreeTranscriptFileName.required &&
        errors.push("Bachelor degree transcripts are required");
      return errors;
    },

    mastersDegreeErrors() {
      const errors = [];
      if (!this.$v.mastersDegreeFileName.$dirty) return errors;
      !this.$v.mastersDegreeFileName.required &&
        errors.push(
          "Your masters degree certificate or proof of enrollment is required"
        );
      return errors;
    },

    mastersDegreeTranscriptErrors() {
      const errors = [];
      if (!this.$v.mastersDegreeTranscriptFileName.$dirty) return errors;
      !this.$v.mastersDegreeTranscriptFileName.mastersTranscriptRequired &&
        errors.push("Masters degree transcripts are required");
      return errors;
    },

    academicBackgroundErrors() {
      const errors = [];
      if (!this.$v.background.$dirty) return errors;
      !this.$v.background.required &&
        errors.push("Your academic background is required");
      return errors;
    }
  },
  mixins: [submissionMixin],
  methods: {
    setupAcademicBackground() {
      if (!this.background) {
        return;
      }

      // if the value of the academic background is not included in
      // the academic background options it means that the user
      // selected custom academic background. In this case we need
      // to set Other on the academic background field
      if (!includes(this.academicBackground, this.background)) {
        this.selectedAcademicBackground = "Other";
      } else {
        this.selectedAcademicBackground = this.background;
      }
    },
    listItemClass(index) {
      return index > 0 ? "mt-4" : null;
    },
    degreeItemClicked(item) {
      this.selectedItemKey = item.key;
    },
    async academicBackgroundChanged(value) {
      if (value !== "Other") {
        this.savingAcademicBackground = true;

        try {
          await this.updateProfileField({
            fieldName: "background",
            fieldValue: value
          });

          this.background = value;
        } finally {
          this.savingAcademicBackground = false;
        }
      }
    },
    async customAcademicBackgroundChanged(value) {
      await this.updateProfileField({
        fieldName: "background",
        fieldValue: value
      });
    },

    validateForm() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        if (
          this.$v.bachelorDegreeFileName.$invalid ||
          this.$v.bachelorDegreeTranscriptFileName.$invalid
        ) {
          this.item = 0;
          this.selectedItemKey = "bachelor";
        } else if (
          this.$v.mastersDegreeFileName.$invalid ||
          this.$v.mastersDegreeTranscriptFileName.$invalid
        ) {
          this.item = 1;
          this.selectedItemKey = "master";
        }
      }

      return !this.$v.$invalid;
    }
  }
};
</script>